import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from '../ui/sheet';
import {
  FilePdf, Sparkle, FileDoc, LockSimple, ClockCounterClockwise,
  SealCheck, ArrowCounterClockwise,
} from '@phosphor-icons/react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { STATUS_CONFIG, SectionEditor, DocBanner, getChantierSummary } from '../DocumentShared';
import { parseSections } from '../DocumentShared';

export function DocumentViewSheet({
  viewDoc, onClose, chantiers, getChantierName,
  viewSections, setSectionsWithDirty,
  viewDirty, enriching, submitting,
  onEnrich, onValidate, onNewVersion,
  onDownloadPdf, onDownloadDocx, onSave, onCancel,
  onShowVersionHistory,
}) {
  return (
    <Sheet open={!!viewDoc} onOpenChange={(open) => { if (!open) onClose(); }}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto" aria-describedby="view-desc">
        <SheetHeader>
          <SheetTitle className="truncate pr-8">{viewDoc?.nom}</SheetTitle>
          <p id="view-desc" className="text-sm text-[#4B5563]">{viewDoc && getChantierName(viewDoc.chantier_id)}</p>
        </SheetHeader>

        {viewDoc && (
          <div className="space-y-4 mt-2">
            <DocBanner name={viewDoc.nom} chantierName={getChantierName(viewDoc.chantier_id)}
              version={viewDoc.version} status={viewDoc.status} date={viewDoc.created_at} />

            {viewDoc.status === 'valide' && (
              <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm text-emerald-700" data-testid="view-locked-banner">
                <LockSimple size={18} weight="fill" />
                <span>Document valide et verrouille (V{viewDoc.version}). Creez une nouvelle version pour modifiér.</span>
              </div>
            )}

            <div className="flex gap-2 flex-wrap">
              {viewDoc.status !== 'valide' && (
                <>
                  <Button variant="outline" size="sm" onClick={onEnrich} disabled={enriching}
                    className="h-10 text-purple-600 border-purple-200 hover:bg-purple-50" data-testid="view-enrich-btn">
                    {enriching ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Sparkle size={16} className="mr-1" />}
                    Completer
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => onValidate(viewDoc)}
                    className="h-10 text-emerald-600 border-emerald-200" data-testid="view-validate-btn">
                    <SealCheck size={16} className="mr-1" /> Valider
                  </Button>
                </>
              )}
              {viewDoc.status === 'valide' && (
                <>
                  <Button variant="outline" size="sm" onClick={() => onDownloadPdf(viewDoc)} className="h-10 text-[#FF6B00]" data-testid="view-pdf-btn">
                    <FilePdf size={16} className="mr-1" /> PDF
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => onDownloadDocx(viewDoc)} className="h-10 text-blue-600" data-testid="view-docx-btn">
                    <FileDoc size={16} className="mr-1" /> Word
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => onNewVersion(viewDoc)}
                    className="h-10 text-blue-600" data-testid="view-newversion-btn">
                    <ArrowCounterClockwise size={16} className="mr-1" /> Nouvelle version
                  </Button>
                </>
              )}
              <Button variant="outline" size="sm" onClick={onShowVersionHistory}
                className="h-10 text-gray-600" data-testid="view-history-btn">
                <ClockCounterClockwise size={16} className="mr-1" /> Historique
              </Button>
            </div>

            <SectionEditor
              sections={viewSections}
              setSections={viewDoc.status === 'valide' ? () => toast.error('Document verrouille') : setSectionsWithDirty}
              chantierId={viewDoc.chantier_id}
              typeDoc={viewDoc.type_document}
              isLocked={viewDoc.status === 'valide'}
              summary={(() => {
                const c = chantiers.find(ch => ch.id === viewDoc.chantier_id);
                return getChantierSummary(c);
              })()}
            />

            {viewDirty && viewDoc.status !== 'valide' && (
              <div className="sticky bottom-0 bg-white py-3 border-t flex gap-3">
                <Button variant="outline" onClick={onCancel} className="flex-1 h-12">
                  Annuler
                </Button>
                <Button onClick={onSave} disabled={submitting}
                  className="flex-1 h-12 bg-[#FF6B00] hover:bg-orange-700" data-testid="view-save-btn">
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Enregistrér'}
                </Button>
              </div>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
